import { useEffect, useMemo, useState } from 'react';
import { useVersionedApi } from '../../utils/versionedApi';
import { usePayPeriods } from '../../hooks/usePayPeriods';
import { useIntegratedSidebar } from '../../hooks/useIntegratedSidebar';
import { resolveOrganizationIdentity } from '../../hooks/useOrganizationTheme';
import { hasWeCareWorkforceSuite } from '../utils/workforceFeatureAccess';
import useMyPlansToRead from '../../components/program245d/planAcknowledgments/useMyPlansToRead';

/**
 * Loads the field-staff web home, all at once. Every request is scoped to the
 * signed-in person on the server: their own visits (hh-today and the visit
 * lists self-scope field staff), forms, assigned clients, leave, messages and
 * timesheet. Each part settles on its own: 'ok', 'hidden' (refused or not
 * offered to this person, e.g. a driver has no Home Health visit list) or
 * 'error'.
 */
const settle = (result, pick) => {
  if (result.status === 'fulfilled') return { status: 'ok', data: pick(result.value?.data) };
  const code = result.reason?.response?.status;
  if (code === 403 || code === 404) return { status: 'hidden', data: null };
  console.error('Field staff home: a section failed to load', { status: code || 'network' });
  return { status: 'error', data: null };
};

const LOADING = { status: 'loading', data: null };
const HIDDEN = { status: 'hidden', data: null };
// A part this person is not offered: settles as hidden without a request.
const NOT_OFFERED = Promise.reject(Object.assign(new Error('not offered'), { response: { status: 403 } }));
NOT_OFFERED.catch(() => {});
// GET /time-tracking/combined-timesheet admits any one of these.
const TIMESHEET_PERMISSIONS = ['access_time_tracking', 'clinical_access', 'view_all_time_entries'];

// "Needs you" shows the dates of the first few visits; the count is the total.
const NEEDS_PREVIEW = 3;
const visitPreview = (d) => ({ count: d?.pagination?.total ?? 0, items: Array.isArray(d?.data) ? d.data : [] });

export function useFieldStaffHome() {
  const apiClient = useVersionedApi('v1');
  const { periods, currentIndex, loading: periodsLoading } = usePayPeriods();
  const period = currentIndex === null || currentIndex === undefined ? null : periods?.[currentIndex] || null;
  // Ask only for what this person may read (QA M2, 2026-10-20: every load
  // logged 403s). Time off is the WeCare workforce suite (the server's
  // requireWeCareOrganization); the timesheet needs time-tracking access.
  const offersTimeOff = useMemo(() => hasWeCareWorkforceSuite(resolveOrganizationIdentity().slug), []);
  const { hasPermission, loading: permissionsLoading } = useIntegratedSidebar();
  const offersTimesheet = TIMESHEET_PERMISSIONS.some((key) => hasPermission(key));
  // 245D staff read and acknowledge their clients' plans (Plans to read).
  // Asked only of people with 245D access, once permissions are known.
  const plans = useMyPlansToRead({ enabled: !permissionsLoading && hasPermission('view_245d_basic') });

  const [parts, setParts] = useState({
    today: LOADING, unsigned: LOADING, flagged: LOADING, forms: LOADING, pendingForms: LOADING,
    clients: LOADING, balances: LOADING, requests: LOADING, messages: LOADING,
  });
  const [timesheet, setTimesheet] = useState(LOADING);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      apiClient.get('/dashboard/lists/hh-today'),
      apiClient.get('/dashboard/lists/unsigned-visits', { params: { pageSize: NEEDS_PREVIEW } }),
      apiClient.get('/dashboard/lists/flagged-visits', { params: { pageSize: NEEDS_PREVIEW } }),
      // kinds=hr: this page lists HR forms too (it only names them; they are signed on the phone).
      apiClient.get('/staff-forms/mine/pending-count', { params: { kinds: 'hr' } }),
      apiClient.get('/staff-forms/mine', { params: { kinds: 'hr' } }),
      apiClient.get('/patients/assigned'),
      offersTimeOff ? apiClient.get('/time-off-balances') : NOT_OFFERED,
      offersTimeOff ? apiClient.get('/time-off-requests', { params: { pageSize: 50 } }) : NOT_OFFERED,
      apiClient.get('/communication/conversations'),
    ]).then(([today, unsigned, flagged, forms, pendingForms, clients, balances, requests, messages]) => {
      if (cancelled) return;
      setParts({
        today: settle(today, (d) => d?.data || null),
        unsigned: settle(unsigned, visitPreview),
        flagged: settle(flagged, visitPreview),
        forms: settle(forms, (d) => d?.data?.count ?? 0),
        pendingForms: settle(pendingForms, (d) => (Array.isArray(d?.data) ? d.data : []).filter((f) => f.status === 'sent')),
        clients: settle(clients, (d) => (Array.isArray(d) ? d : d?.data || [])),
        balances: settle(balances, (d) => d?.data || []),
        requests: settle(requests, (d) => d?.data || []),
        messages: settle(messages, (d) => d),
      });
    });
    return () => { cancelled = true; };
  }, [apiClient, offersTimeOff]);

  useEffect(() => {
    if (periodsLoading || permissionsLoading) return undefined;
    if (!period || !offersTimesheet) { setTimesheet(HIDDEN); return undefined; }
    let cancelled = false;
    apiClient.get('/time-tracking/combined-timesheet', { params: { startDate: period.start, endDate: period.end } })
      .then((res) => { if (!cancelled) setTimesheet({ status: 'ok', data: res.data?.totals || null }); })
      .catch((err) => { if (!cancelled) setTimesheet(settle({ status: 'rejected', reason: err }, () => null)); });
    return () => { cancelled = true; };
  }, [apiClient, period, periodsLoading, permissionsLoading, offersTimesheet]);

  return { parts, timesheet, period, plans };
}

export default useFieldStaffHome;
